import store from "@/store/index";
import * as Sentry from "@sentry/vue";
import Vue from "vue";
import router from "./router/";

if (window.App.state.config.sentry.dsn) {
    Sentry.init({
        app: Vue,
        dsn: window.App.state.config.sentry.dsn,
        environment: window.App.state.config.app.env || "production",
        // Setting this option to true will send default PII data to Sentry.
        // For example, automatic IP address collection on events
        sendDefaultPii: true,
        // Add allowUrls to prevent CORS issues
        allowUrls: [window.location.origin, /https?:\/\/.*\..*\//],
        // Comprehensive list of errors to ignore
        ignoreErrors: [
            "ResizeObserver loop limit exceeded",
            "ResizeObserver loop completed with undelivered notifications.",
            /Network Error/i,
            /Navigation cancelled/i,
            /Redirected when going from/i,
            /Non-Error promise rejection captured/i,
            /Validating a non-existent field/i,
            /Request aborted/i,
            /NavigationDuplicated/i,
            /UnhandledRejection/i,
            /CORS/i,
            /Cross-Origin Request Blocked/i,
            /The Same Origin Policy disallows reading/i,
            /Failed to fetch/i,
            /NetworkError/i,
            /Load failed/i,
            /Loading chunk \d+ failed/i,
            /ChunkLoadError/i,
            "Script error.",
            "SecurityError",
            "Permission denied",
            "Blocked by Content Security Policy",
            /blocked:mixed-content/i,
            /SyntaxError: Unexpected token '<'/i
        ],
        integrations: [
            Sentry.browserTracingIntegration({
                router: router
            })
        ],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,
        beforeSend(event, hint) {
            const error = hint.originalException;
            let status = _.get(
                error,
                "status",
                _.get(error, "response.status")
            );

            // More comprehensive network/CORS error filtering
            if (error) {
                const errorMessage = error.message || error.toString() || "";
                const errorName = error.name || "";
                const errorStack = error.stack || "";

                // Check error message, name, and stack for network/CORS patterns
                const networkPatterns = [
                    /cors/i,
                    /cross-origin/i,
                    /same.origin.policy/i,
                    /failed.to.fetch/i,
                    /network.error/i,
                    /load.failed/i,
                    /loading.chunk.*failed/i,
                    /chunklisterror/i,
                    /securityerror/i,
                    /permission.denied/i,
                    /blocked.*content.security.policy/i,
                    /blocked:mixed-content/i,
                    /script.error/i,
                    /unexpected.token.*</i
                ];

                const isNetworkError = networkPatterns.some(
                    (pattern) =>
                        pattern.test(errorMessage) ||
                        pattern.test(errorName) ||
                        pattern.test(errorStack)
                );

                if (isNetworkError) {
                    console.log("Sentry: Ignoring network/CORS error:", {
                        message: errorMessage,
                        name: errorName,
                        stack: errorStack
                    });
                    return null;
                }
            }

            // Ignore specific HTTP status codes
            if ([401, 419, 429].includes(status)) {
                console.log(`Sentry: ignoring ${status} status code`);
                return null;
            }

            // Filter out events that are likely false positives
            if (event.exception && event.exception.values) {
                for (const exception of event.exception.values) {
                    const exceptionMessage = exception.value || "";
                    const exceptionType = exception.type || "";

                    // Skip generic script errors and network issues
                    if (
                        exceptionType === "Script error" ||
                        exceptionMessage === "Script error." ||
                        exceptionMessage.includes("Loading chunk") ||
                        exceptionMessage.includes("ChunkLoadError")
                    ) {
                        console.log(
                            "Sentry: Ignoring generic/chunk error:",
                            exceptionMessage
                        );
                        return null;
                    }
                }
            }

            // Only show report dialog for genuine exceptions in production
            if (
                event.exception &&
                window.App.state.config.app.env === "production"
            ) {
                let user = store.state.user;
                // Add a small delay to ensure the error is actually sent first
                setTimeout(() => {
                    Sentry.showReportDialog({
                        eventId: event.event_id,
                        user: {
                            email: _.get(user, "email"),
                            name: _.get(user, "name")
                        }
                    });
                }, 100);
            }

            return event;
        },
        // Add additional configuration to prevent CORS issues
        beforeSendTransaction(event) {
            // Ignore transactions that might cause CORS issues
            if (event.request && event.request.url) {
                const url = event.request.url;
                if (url.includes("sentry") || url.includes("chunk")) {
                    return null;
                }
            }
            return event;
        }
    });
}
